import { ErrorResponse, ServiceResponse, ZipCode } from '@bzbs/react-api-client';
import React, { createContext, useContext, useState } from 'react';
import { useBuzzebeesServiceContext } from './buzzebees_service_provider';

export type ZipCodeDropDownItem = {
  name: string;
  id: string;
};

export type ZipCodeContextType = {
  zipCodes: ZipCode[];
  provinceDropDownItems: ZipCodeDropDownItem[];
  districtDropDownItems: ZipCodeDropDownItem[];
  subDistrictDropDownItems: ZipCodeDropDownItem[];
  selectedProvinceDropDownItem: ZipCodeDropDownItem | null;
  selectedDistrictDropDownItem: ZipCodeDropDownItem | null;
  selectedSubDistrictDropDownItem: ZipCodeDropDownItem | null;
  isLoading: boolean;
  error: ErrorResponse | null;
  actions: {
    fetchZipCodes: (zipCode: string) => Promise<ServiceResponse<ZipCode[]>>;
    setSelectedProvinceDropDownItem: (item: ZipCodeDropDownItem | null) => void;
    setSelectedDistrictDropDownItem: (item: ZipCodeDropDownItem | null) => void;
    setSelectedSubDistrictDropDownItem: (item: ZipCodeDropDownItem | null) => void;
    clear(): void;
  };
};

const ZipCodeContext = createContext<ZipCodeContextType>({
  zipCodes: [],
  provinceDropDownItems: [],
  districtDropDownItems: [],
  subDistrictDropDownItems: [],
  selectedProvinceDropDownItem: null,
  selectedDistrictDropDownItem: null,
  selectedSubDistrictDropDownItem: null,
  isLoading: false,
  error: null,
  actions: {
    fetchZipCodes: async () => ({ type: 'client-error', message: 'Not implemented' }),
    setSelectedProvinceDropDownItem: () => {},
    setSelectedDistrictDropDownItem: () => {},
    setSelectedSubDistrictDropDownItem: () => {},
    clear: () => {},
  },
});

export function useZipCodeContext() {
  return useContext(ZipCodeContext);
}

function getDistinctZipCodeDropDownItems(items: ZipCodeDropDownItem[]): ZipCodeDropDownItem[] {
  const uniqueItems = new Map<string, ZipCodeDropDownItem>();
  items.forEach((item) => {
    if (!uniqueItems.has(item.id)) {
      uniqueItems.set(item.id, item);
    }
  });
  return Array.from(uniqueItems.values());
}

export function ZipCodeProvider({ children }: { children: React.ReactNode }) {
  const [zipCodes, setZipCodes] = useState<ZipCode[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<ErrorResponse | null>(null);

  const [selectedProvinceDropDownItem, setSelectedProvinceDropDownItem] = useState<ZipCodeDropDownItem | null>(null);
  const [selectedDistrictDropDownItem, setSelectedDistrictDropDownItem] = useState<ZipCodeDropDownItem | null>(null);
  const [selectedSubDistrictDropDownItem, setSelectedSubDistrictDropDownItem] = useState<ZipCodeDropDownItem | null>(
    null
  );

  const provinceDropDownItems = getDistinctZipCodeDropDownItems(
    zipCodes.map(
      (zipCode) =>
        ({
          name: zipCode.ProvinceName,
          id: zipCode.ProvinceCode,
        }) as ZipCodeDropDownItem
    )
  );

  const districtDropDownItems = getDistinctZipCodeDropDownItems(
    zipCodes
      .filter((zipCode) => zipCode.ProvinceCode === selectedProvinceDropDownItem?.id)
      .map(
        (zipCode) =>
          ({
            name: zipCode.DistrictName,
            id: zipCode.DistrictCode,
          }) as ZipCodeDropDownItem
      )
  );

  const subDistrictDropDownItems = getDistinctZipCodeDropDownItems(
    zipCodes
      .filter((zipCode) => zipCode.DistrictCode === selectedDistrictDropDownItem?.id)
      .map(
        (zipCode) =>
          ({
            name: zipCode.SubDistrictName,
            id: zipCode.SubDistrictCode,
          }) as ZipCodeDropDownItem
      )
  );

  const { bzbsService } = useBuzzebeesServiceContext();

  const fetchZipCodes = async (zipCode: string): Promise<ServiceResponse<ZipCode[]>> => {
    setIsLoading(true);
    const response = await bzbsService.addressApi.zipCodes({ zipCode });
    setIsLoading(false);

    if (response.type === 'success') {
      setZipCodes(response.model);
      setError(null);
    } else {
      setError(response);
    }

    return response;
  };

  const clear = () => {
    setZipCodes([]);
    setSelectedProvinceDropDownItem(null);
    setSelectedDistrictDropDownItem(null);
    setSelectedSubDistrictDropDownItem(null);
    setError(null);
  };

  return (
    <ZipCodeContext.Provider
      value={{
        zipCodes,
        provinceDropDownItems,
        districtDropDownItems,
        subDistrictDropDownItems,
        selectedProvinceDropDownItem,
        selectedDistrictDropDownItem,
        selectedSubDistrictDropDownItem,
        isLoading,
        error,
        actions: {
          fetchZipCodes,
          setSelectedProvinceDropDownItem,
          setSelectedDistrictDropDownItem,
          setSelectedSubDistrictDropDownItem,
          clear,
        },
      }}
    >
      {children}
    </ZipCodeContext.Provider>
  );
}
